import { atom, atomFamily, useRecoilState } from "recoil";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { axios } from "./lib/request";
import { useQueryKey } from "./organize/Organize.state";
import { nubsQueryKey } from "./lib/refresh";
import { alertState } from "./common/AlertDisplay/AlertDisplay.state";
import { useAuthUser } from "react-auth-kit";
import { getErrorMessage } from "./lib/error";

export const refreshState = atomFamily({
  key: "global/refresh",
  default: (uri) => false,
});

export const useVideoRefresher = (game_id) => {
  const queryClient = useQueryClient();
  const queryKey = useQueryKey(game_id);

  const { mutate } = useMutation((message) => {
    const { uuid } = message.detail.media;
    const { id } = message.detail.media.relation;
    const data = queryClient.getQueryData(queryKey);

    if (!data) {
      return;
    }

    const taskIndex = data.tasks.findIndex((task) => task.id === id);

    if (taskIndex === -1) {
      return;
    }

    const defaultTask = {
      placeholders: [],
      assets: {
        videos: [],
      },
    }
    const task = (data.tasks || [])[taskIndex] || defaultTask;

    task.placeholders = task.placeholders || defaultTask.placeholders
    task.assets = task.assets || defaultTask.assets
    const placeholderIndex = task.placeholders.findIndex(
      (placeholder) => placeholder.uuid === uuid
    );
    const { key } = task.placeholders?.[placeholderIndex] || {};
    const videoIndex = task.assets.videos.findIndex(
      (video) => video.name === key
    );
    const video = (task.assets?.videos || [])[videoIndex] || {};
    queryClient.setQueryData(queryKey, {
      ...data,
      tasks: [
        ...data.tasks.slice(0, taskIndex),
        {
          ...data.tasks[taskIndex],
          assets: {
            ...task.assets,
            videos: [
              ...task.assets.videos.slice(0, videoIndex),
              {
                ...video,
                urls: {
                  ...video?.urls,
                  png: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/images/${uuid}-00001.png`,
                },
              },
              ...task.assets.videos.slice(videoIndex + 1),
            ],
          },
          // live assets flag marks dynamic updates
          // on ss.game.media event listener
          liveAssets: true,
          liveAssetsId: uuid,
        },
        ...data.tasks.slice(taskIndex + 1),
      ],
    });
  });
  return mutate;
};

export const useNubsTaskRefresher = (game_id, task_id) => {
  const queryClient = useQueryClient();
  const queryKey = nubsQueryKey(game_id, task_id);

  const { mutate } = useMutation((message) => {
    const { uuid } = message.detail.media;
    const data = queryClient.getQueryData(queryKey);
    if (!data) {
      return;
    }
    const nubsIndex = data.nubs.findIndex((nub) => (nub?.assets?.videos || []).find(v => v.name  === uuid));

    if (nubsIndex === -1) {
      return;
    }

    const defaultNub = {
      nubs: [],
      assets: {
        videos: [],
      },
    }
    const nub = (data.nubs || [])[nubsIndex] || defaultNub;

    nub.assets = nub.assets || defaultNub.assets
    const videoIndex = nub.assets.videos.findIndex(
      (video) => video.name === uuid
    );
    const video = (nub.assets?.videos || [])[videoIndex] || {};
    const nubIndex = nub.nubs.findIndex(
      (nub) => nub.media_uuid === uuid
    );
    const nubs = [...nub.nubs]
    nubs.splice(nubIndex, 1, {
      ...nub.nubs[nubIndex],
    })

    queryClient.setQueryData(queryKey, {
      ...data,
      nubs: [
        ...data.nubs.slice(0, nubsIndex),
        {
          ...data.nubs[nubsIndex],
          nubs,
          assets: {
            ...nub.assets,
            videos: [
              ...nub.assets.videos.slice(0, videoIndex),
              {
                ...video,
                urls: {
                  ...video?.urls,
                  png: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/images/${uuid}-00001.png`,
                },
              },
              ...nub.assets.videos.slice(videoIndex + 1),
            ],
          },
          // live assets flag marks dynamic updates
          // on ss.game.media event listener;
          liveAssets: true,
          liveAssetsId: uuid,
        },
        ...data.nubs.slice(nubsIndex + 1),
      ],
    });
  });
  return mutate;
};

export const useNubsGameRefresher = (game_id) => {
  const queryClient = useQueryClient();
  const queryKey = nubsQueryKey(game_id);

  const { mutate } = useMutation((message) => {
    const { uuid } = message.detail.media;
    const data = queryClient.getQueryData(queryKey);

    if (!data) {
      return;
    }

    const videoIndex = data.assets.videos.findIndex(
      (video) => video.name === uuid
    );

    if (videoIndex === -1) {
      return;
    }

    const video = (data.assets?.videos || [])[videoIndex] || {};
    queryClient.setQueryData(queryKey, {
      ...data,
      assets: {
        ...data.assets,
        videos: [
          ...data.assets.videos.slice(0, videoIndex),
          {
            ...video,
            urls: {
              ...video?.urls,
              png: `https://${process.env.REACT_APP_WHITELABEL}-assets.socialscavenger.com/images/${uuid}-00001.png`,
            },
          },
          ...data.assets.videos.slice(videoIndex + 1),
        ],
      },
      // live assets flag marks dynamic updates
      // on ss.game.media event listener
      liveAssets: true,
      liveAssetsId: uuid,
    });
  });
  return mutate;
};

export const videoQueue = atom({
  key: "global/videoQueue",
  default: [],
});

const getActionContext = (actionContext, details) => {
  return typeof actionContext === "function" ? actionContext(details) : actionContext;
};

/**
 * A generic React Query hook for managing individual items within a collection.
 * Provides optimistic updates, error handling via global alerts, and cache invalidation.
 * 
 * @param {Object} data - The full dataset containing the collection.
 * @param {string|number} id - The ID of the item to manage.
 * @param {string} name - The key of the collection within the data object.
 * @param {Array|string} queryKey - The React Query key to invalidate/update.
 * @param {Function} setter - The mutation function to call with updated params.
 * @param {Object} [config={}] - Additional configuration (e.g., invalidation rules, action context).
 * @returns {Array} A tuple containing the item object and the setter function.
 */
export const useItemQuery = (data, id, name, queryKey, setter, config = {}) => {
  const [, setAlert] = useRecoilState(alertState);
  const { invalidate = true, invalidateKey, actionContext, ...invalidationConfig } = config
  const queryClient = useQueryClient();
  const collection = name ? data[name] : data;
  const index = collection.findIndex((item) => item.id === id);
  const item = collection[index];
  const setItem = useCallback(
    async (params) => {
      queryClient.cancelQueries(queryKey);
      queryClient.setQueryData(queryKey, {
        ...data,
        [name]: [
          ...collection.slice(0, index),
          { ...item, ...params },
          ...collection.slice(index + 1),
        ],
      });

      const { assets, placeholders, tour, ...rest } = params;

      try {
        if (Object.keys(rest).length) {
          await setter(rest);
        }
      } catch (error) {
        const message = getErrorMessage(error, getActionContext(actionContext, {
          id,
          item,
          name,
          params: rest,
        }));
        setAlert({ type: "error", message });
      }
      
      const shouldInvalidate = (!assets && !placeholders && !tour) && invalidate 
      if (shouldInvalidate) { 
        queryClient.invalidateQueries((invalidateKey || queryKey), { ...invalidationConfig }); 
      } 
    },
    [collection, data, id, index, item, name, queryClient, queryKey, setter, invalidate, invalidateKey, invalidationConfig, actionContext, setAlert]
  );

  return [item, setItem];
};

export const fetchS3Policy = async () => {
  const url = "https://app.socialscavenger.com/v1/upload.json";
  const { data } = await axios.get(url);
  return data;
};

/**
 * A React Query hook that fetches and caches the AWS S3 upload policy.
 * Keeps the policy cached indefinitely while the user is logged in to minimize API calls.
 * 
 * @returns {Object} The React Query result object containing the S3 policy.
 */
export const useUploadPolicyQuery = () => {
  const auth = useAuthUser()();
  const userKey = auth?.user_id || "anonymous";

  // Keep upload policy in cache indefinitely as long as the user is logged in
  return useQuery(["uploadPolicy", userKey], () => fetchS3Policy(), {
    suspense: true,
    keepPreviousData: true,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};
